// ../../react-shim.js
import React from "react";

// src/array.ts
var addItem = (array, item) => {
  return [...array, item];
};
var removeItem = (array, item) => {
  return array.filter((eachItem) => eachItem !== item);
};

// src/assertions.ts
var isFunction = (value) => {
  return typeof value === "function";
};
var isNumber = (value) => {
  return typeof value === "number";
};
var isNotNumber = (value) => typeof value !== "number" || Number.isNaN(value) || !Number.isFinite(value);
var isArray = (value) => {
  return Array.isArray(value);
};
var isEmptyArray = (value) => isArray(value) && value.length === 0;
var isUndefined = (value) => typeof value === "undefined" || value === void 0;
var isObject = (value) => {
  const type = typeof value;
  return value !== null && (type === "object" || type === "function") && !isArray(value);
};
var isEmptyObject = (value) => isObject(value) && !Object.keys(value).length;
var isNotEmptyObject = (value) => value && !isEmptyObject(value);
var isNull = (value) => value === null;
var isString = (value) => Object.prototype.toString.call(value) === "[object String]";
var isInputEvent = (value) => value && isObject(value) && isObject(value.target);
var __DEV__ = process.env.NODE_ENV !== "production";

// src/dom.ts
var _window;
function isElement(el) {
  return el != null && typeof el == "object" && "nodeType" in el && el.nodeType === Node.ELEMENT_NODE;
}
try {
  _window = window;
} catch {
}
var getWindow = (node) => {
  var _a;
  return ((_a = node == null ? void 0 : node.ownerDocument) == null ? void 0 : _a.defaultView) ?? _window;
};
var checkIsBrowser = () => {
  const __window = getWindow();
  return Boolean(
    typeof __window !== "undefined" && __window.document && __window.document.createElement
  );
};
var isBrowser = checkIsBrowser();
var getOwnerDocument = (node) => {
  return isElement(node) ? node.ownerDocument ?? document : document;
};
var getActiveElement = (node) => {
  const doc = getOwnerDocument(node);
  return doc == null ? void 0 : doc.activeElement;
};
var normalizeEventKey = (event) => {
  const { key, keyCode } = event;
  const isArrowKey = keyCode >= 37 && keyCode <= 40 && String(key).indexOf("Arrow") !== 0;
  const eventKey = isArrowKey ? `Arrow${key}` : key;
  return eventKey;
};
var dataAttr = (condition) => condition ? "" : void 0;
var ariaAttr = (condition) => condition ? true : void 0;
var cx = (...classNames) => classNames.filter(Boolean).join(" ");

// src/tabbable.ts
var hasDisplayNone = (element) => {
  var _a;
  return ((_a = _window) == null ? void 0 : _a.getComputedStyle(element).display) === "none";
};
var hasTabIndex = (element) => element.hasAttribute("tabindex");
var hasNegativeTabIndex = (element) => hasTabIndex(element) && element.tabIndex === -1;
var isDisabled = (element) => {
  return Boolean(element.getAttribute("disabled")) === true || Boolean(element.getAttribute("aria-disabled")) === true;
};
var hasFocusWithin = (element) => {
  if (!document.activeElement)
    return false;
  return element.contains(document.activeElement);
};
var isHTMLElement = (element) => {
  return element instanceof HTMLElement;
};
var isHidden = (element) => {
  if (element.parentElement && isHidden(element.parentElement))
    return true;
  return element.hidden;
};
var isContentEditable = (element) => {
  const value = element.getAttribute("contenteditable");
  return value && value !== void 0;
};
var isFocusable = (element) => {
  if (!isHTMLElement(element) || isHidden(element) || isDisabled(element)) {
    return false;
  }
  const { localName } = element;
  const focusableTags = ["input", "select", "textarea", "button"];
  if (focusableTags.includes(localName))
    return true;
  const others = {
    a: () => element.hasAttribute("href"),
    audio: () => element.hasAttribute("controls"),
    video: () => element.hasAttribute("controls")
  };
  if (localName in others) {
    return others[localName]();
  }
  if (isContentEditable(element))
    return true;
  return hasTabIndex(element);
};
var isTabbable = (element) => {
  return isHTMLElement(element) && isFocusable(element) && !hasNegativeTabIndex(element);
};
var isActiveElement = (element) => {
  const doc = isHTMLElement(element) ? getOwnerDocument(element) : document;
  return doc.activeElement === element;
};
var isInputElement = (element) => {
  return isHTMLElement(element) && element.tagName.toLowerCase() === "input" && "select" in element;
};

// src/dom-query.ts
var SELECTORS = [
  "input",
  "select",
  "textarea",
  "a[href]",
  "area[href]",
  "button",
  "[tabindex]",
  "audio[controls]",
  "video[controls]",
  "[contenteditable]:not([contenteditable=false])"
];
var selector = SELECTORS.join();
var getAllFocusable = (container) => {
  const allFocusable = Array.from(container.querySelectorAll(selector));
  allFocusable.unshift(container);
  return allFocusable.filter(isFocusable).filter((el) => window.getComputedStyle(el).display !== "none");
};
var getAllTabbable = (container, fallbackToFocusable) => {
  const allFocusable = Array.from(container.querySelectorAll(selector));
  const allTabbable = allFocusable.filter(isTabbable);
  if (isTabbable(container)) {
    allTabbable.unshift(container);
  }
  if (!allTabbable.length && fallbackToFocusable) {
    return allFocusable;
  }
  return allTabbable;
};
var getFirstTabbableIn = (container, fallbackToFocusable) => {
  const [first] = getAllTabbable(container, fallbackToFocusable);
  return first || null;
};

// src/logger.ts
var warn = (options) => {
  if (options.condition && __DEV__) {
    console.warn(options.message);
  }
};

// src/focus.ts
var supportsPreventScrollCached = null;
var supportsPreventScroll = () => {
  if (supportsPreventScrollCached === null) {
    supportsPreventScrollCached = false;
    try {
      const div = document.createElement("div");
      div.focus({
        get preventScroll() {
          supportsPreventScrollCached = true;
          return true;
        }
      });
    } catch (e) {
    }
  }
  return supportsPreventScrollCached;
};
var getScrollableElements = (element) => {
  const doc = getOwnerDocument(element);
  const win = doc.defaultView ?? window;
  let parent = element.parentNode;
  const scrollableElements = [];
  const rootScrollingElement = doc.scrollingElement || doc.documentElement;
  while (parent instanceof win.HTMLElement && parent !== rootScrollingElement) {
    if (parent.offsetHeight < parent.scrollHeight || parent.offsetWidth < parent.scrollWidth) {
      scrollableElements.push({
        element: parent,
        scrollTop: parent.scrollTop,
        scrollLeft: parent.scrollLeft
      });
    }
    parent = parent.parentNode;
  }
  if (rootScrollingElement instanceof win.HTMLElement) {
    scrollableElements.push({
      element: rootScrollingElement,
      scrollTop: rootScrollingElement.scrollTop,
      scrollLeft: rootScrollingElement.scrollLeft
    });
  }
  return scrollableElements;
};
var restoreScrollPosition = (scrollableElements) => {
  for (const { element, scrollTop, scrollLeft } of scrollableElements) {
    element.scrollTop = scrollTop;
    element.scrollLeft = scrollLeft;
  }
};
var focus = (element, options = {}) => {
  const {
    isActive = isActiveElement,
    preventScroll = true,
    selectTextIfInput = true,
    nextTick
  } = options;
  if (!element || isActive(element))
    return -1;
  const triggerFocus = () => {
    if (!element) {
      warn({
        condition: true,
        message: "[nature-ui]: can't call focus() on `null` or `undefined` element"
      });
      return;
    }
    if (supportsPreventScroll()) {
      element.focus({ preventScroll });
    } else {
      element.focus();
      if (preventScroll) {
        const scrollableElements = getScrollableElements(
          element
        );
        restoreScrollPosition(scrollableElements);
      }
    }
    if (isInputElement(element) && selectTextIfInput) {
      element.select();
    }
  };
  if (nextTick) {
    return requestAnimationFrame(triggerFocus);
  }
  triggerFocus();
  return -1;
};

// src/functions.ts
var runIfFn = (valueOrFn, ...args) => {
  return isFunction(valueOrFn) ? valueOrFn(...args) : valueOrFn;
};
var callAllHandler = (...fns) => {
  return (event) => {
    fns.some((fn) => {
      fn == null ? void 0 : fn(event);
      return event == null ? void 0 : event.defaultPrevented;
    });
  };
};

// src/numbers.ts
var minSafeInteger = Number.MIN_SAFE_INTEGER || -9007199254740991;
var maxSafeInteger = Number.MAX_SAFE_INTEGER || 9007199254740991;
var toNumber = (value) => {
  const num = parseFloat(value);
  return isNotNumber(num) ? 0 : num;
};
var toPrecision = (value, precision) => {
  let nextValue = toNumber(value);
  const _pres = precision ?? 10;
  const scaleFactor = 10 ** _pres;
  nextValue = Math.round(nextValue * scaleFactor) / scaleFactor;
  return precision ? nextValue.toFixed(precision) : nextValue.toString();
};
var countDecimalPlaces = (value) => {
  if (!Number.isFinite(value))
    return 0;
  let e = 1;
  let p = 0;
  while (Math.round(value * e) / e !== value) {
    e *= 10;
    p++;
  }
  return p;
};
var valueToPercent = (value, min, max) => (value - min) * 100 / (max - min);
var clampValue = (value, min, max) => {
  if (value === null)
    return value;
  warn({
    condition: max < min,
    message: "[Nature UI] clamp: max cannot be less than min"
  });
  return Math.min(Math.max(value, min), max);
};

// src/objects.ts
var omit = (object, keys) => {
  const result = {};
  Object.keys(object).forEach((key) => {
    if (!keys.includes(key)) {
      result[key] = object[key];
    }
  });
  return result;
};
var objectKeys = (obj) => Object.keys(obj);

// src/react-helpers.ts
import React2 from "react";
var createContext = (options = {}) => {
  const {
    strict = true,
    errorMessage = "useContext must be inside a Provider with a value",
    name
  } = options;
  const Context = React2.createContext(void 0);
  Context.displayName = name;
  const useContext = () => {
    const context = React2.useContext(Context);
    if (!context && strict)
      throw new Error(errorMessage);
    return context;
  };
  return [
    Context.Provider,
    useContext,
    Context
  ];
};
var getValidChildren = (children) => React2.Children.toArray(children).filter(
  (child) => React2.isValidElement(child)
);
var assignRef = (ref, value) => {
  if (ref === null || ref === void 0 || !ref)
    return;
  if (isFunction(ref)) {
    ref(value);
    return;
  }
  try {
    ref.current = value;
  } catch (error) {
    throw new Error(`Cannot assign value '${value}' to ref '${ref}'`);
  }
};
var mergeRefs = (...refs) => (value) => {
  refs.forEach((ref) => assignRef(ref, value));
};
export {
  __DEV__,
  _window,
  addItem,
  ariaAttr,
  callAllHandler,
  clampValue,
  countDecimalPlaces,
  createContext,
  cx,
  dataAttr,
  focus,
  getActiveElement,
  getAllFocusable,
  getAllTabbable,
  getFirstTabbableIn,
  getOwnerDocument,
  getValidChildren,
  getWindow,
  hasDisplayNone,
  hasFocusWithin,
  hasNegativeTabIndex,
  hasTabIndex,
  isActiveElement,
  isArray,
  isBrowser,
  isContentEditable,
  isDisabled,
  isElement,
  isEmptyArray,
  isEmptyObject,
  isFocusable,
  isFunction,
  isHTMLElement,
  isHidden,
  isInputElement,
  isInputEvent,
  isNotEmptyObject,
  isNotNumber,
  isNull,
  isNumber,
  isObject,
  isString,
  isTabbable,
  isUndefined,
  maxSafeInteger,
  mergeRefs,
  minSafeInteger,
  normalizeEventKey,
  objectKeys,
  omit,
  removeItem,
  runIfFn,
  toPrecision,
  valueToPercent,
  warn
};
